/** One auto-checked exercise: answer → «Проверить» → feedback sheet → «Продолжить». */
import { useState } from 'react';
import type { Lesson } from '../../core/content.ts';
import {
  correctAnswerText,
  gradeChoice,
  gradeFill,
  gradeOrder,
  type AutoExercise,
  type FillResult,
} from '../../core/exercises.ts';
import { explainExercise } from '../../core/explain.ts';
import { ChoiceView } from './ChoiceView.tsx';
import { FillView } from './FillView.tsx';
import { OrderView } from './OrderView.tsx';
import { FeedbackSheet } from './FeedbackSheet.tsx';

interface Outcome {
  correct: boolean;
  fill?: FillResult;
}

export function ExerciseStep({
  lesson,
  exercise,
  speechLang,
  onAnswered,
  onDone,
}: {
  lesson: Lesson;
  exercise: AutoExercise;
  speechLang: string;
  /** Called once when checked; returns the XP actually granted. */
  onAnswered: (correct: boolean) => number;
  onDone: () => void;
}) {
  const [choice, setChoice] = useState<string[]>([]);
  const [fill, setFill] = useState<string[]>([]);
  const [order, setOrder] = useState<number[]>([]);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [xp, setXp] = useState(0);

  const ready = (() => {
    switch (exercise.kind) {
      case 'choice':
        return choice.length > 0;
      case 'fill':
        return exercise.blanks.every((_, i) => (fill[i] ?? '').trim() !== '');
      case 'order':
        return order.length === exercise.tiles.length;
    }
  })();

  const check = () => {
    let result: Outcome;
    switch (exercise.kind) {
      case 'choice':
        result = { correct: gradeChoice(exercise, choice) };
        break;
      case 'fill': {
        const graded = gradeFill(exercise, fill);
        result = { correct: graded.correct, fill: graded };
        break;
      }
      case 'order':
        result = { correct: gradeOrder(exercise, order.map((i) => exercise.tiles[i]!.surface)) };
        break;
    }
    setOutcome(result);
    setXp(onAnswered(result.correct));
  };

  const checked = outcome !== null;

  return (
    <div className="exercise">
      <p className="t-caps exercise__instruction">{exercise.instruction}</p>
      <div className="exercise__body">
        {exercise.kind === 'choice' && (
          <ChoiceView
            exercise={exercise}
            selected={choice}
            onChange={setChoice}
            checked={checked}
            speechLang={speechLang}
          />
        )}
        {exercise.kind === 'fill' && (
          <FillView exercise={exercise} values={fill} onChange={setFill} result={outcome?.fill ?? null} />
        )}
        {exercise.kind === 'order' && (
          <OrderView
            exercise={exercise}
            order={order}
            onChange={setOrder}
            checked={checked}
            correct={outcome?.correct ?? false}
          />
        )}
      </div>
      {outcome ? (
        <FeedbackSheet
          correct={outcome.correct}
          xp={xp}
          answer={correctAnswerText(exercise)}
          explanation={explainExercise(lesson, exercise)}
          onContinue={onDone}
        />
      ) : (
        <div className="lesson__footer">
          <button type="button" className="btn btn--ink btn--block" disabled={!ready} onClick={check}>
            Проверить
          </button>
        </div>
      )}
    </div>
  );
}
